/**
 * Listings formatting helpers.
 *
 * Backend stores prices in paise (smallest INR unit) to avoid float
 * precision bugs; the frontend formats to readable rupees with the lakh /
 * crore conventions Indian buyers expect.
 */

const RUPEE_SYMBOL = "₹";

/**
 * Convert paise to rupees and format as `₹ 1.25 Cr`, `₹ 35 L`, or `₹ 8,000`
 * depending on magnitude. Numbers below ₹1L are shown with comma
 * separators; ₹1L–₹99L collapse to lakhs; ≥₹1Cr collapses to crores.
 */
export function formatPaiseAsRupees(paise: number): string {
	if (!Number.isFinite(paise)) return `${RUPEE_SYMBOL} —`;
	const rupees = Math.round(paise / 100);

	if (rupees >= 10_000_000) {
		const crores = rupees / 10_000_000;
		return `${RUPEE_SYMBOL} ${trimZeros(crores.toFixed(2))} Cr`;
	}
	if (rupees >= 100_000) {
		const lakhs = rupees / 100_000;
		return `${RUPEE_SYMBOL} ${trimZeros(lakhs.toFixed(2))} L`;
	}
	return `${RUPEE_SYMBOL} ${rupees.toLocaleString("en-IN")}`;
}

/**
 * Parse a free-form price input — accepts plain numbers (`2500000`),
 * lakh shorthand (`25L`, `25 lakh`, `25l`), or crore shorthand (`1.5Cr`,
 * `1.5 cr`). Returns paise as an integer, or null if unparseable.
 *
 * Realtors enter prices in lakhs/crores in the wild — supporting it at
 * the input layer prevents typos like "₹25,00,00,00,00" from "2.5 Cr".
 */
export function parseRupeeInputToPaise(input: string): number | null {
	const trimmed = input.trim();
	if (!trimmed) return null;
	const match = trimmed.match(
		/^([0-9]+(?:\.[0-9]+)?)\s*(cr|crore|crores|l|lakh|lakhs)?\s*$/i,
	);
	if (!match) return null;
	const amount = Number.parseFloat(match[1]);
	if (!Number.isFinite(amount) || amount <= 0) return null;
	const unit = match[2]?.toLowerCase();
	let rupees: number;
	if (unit === "cr" || unit === "crore" || unit === "crores") {
		rupees = amount * 10_000_000;
	} else if (unit === "l" || unit === "lakh" || unit === "lakhs") {
		rupees = amount * 100_000;
	} else {
		rupees = amount;
	}
	const paise = Math.round(rupees * 100);
	if (!Number.isInteger(paise) || paise <= 0) return null;
	return paise;
}

/**
 * Reverse of `parseRupeeInputToPaise` for edit-mode pre-fill. Produces a
 * string the parser will accept and that round-trips exactly when typed
 * back. Sub-lakh values stay as plain rupees.
 */
export function formatPaiseAsRupeeInput(paise: number): string {
	// Derive from precise paise (no rupee-level rounding) so the result
	// round-trips exactly back through parseRupeeInputToPaise. Cr / L
	// shorthand is only safe when the value is an exact integer multiple of
	// a crore / lakh — otherwise emit the plain rupee amount.
	const rupees = paise / 100;
	if (
		Number.isInteger(rupees) &&
		rupees >= 10_000_000 &&
		rupees % 10_000_000 === 0
	) {
		return `${rupees / 10_000_000} Cr`;
	}
	if (Number.isInteger(rupees) && rupees >= 100_000 && rupees % 100_000 === 0) {
		return `${rupees / 100_000} L`;
	}
	return rupees.toString();
}

/**
 * Format a budget range from paise bounds: `₹X – ₹Y` when both are set,
 * `≥ ₹X` / `≤ ₹Y` when only one bound is set, or null when neither is.
 */
export function formatBudgetRange(
	min: number | null,
	max: number | null,
): string | null {
	if (min == null && max == null) return null;
	if (min != null && max != null) {
		return `${formatPaiseAsRupees(min)} – ${formatPaiseAsRupees(max)}`;
	}
	if (min != null) return `≥ ${formatPaiseAsRupees(min)}`;
	if (max != null) return `≤ ${formatPaiseAsRupees(max)}`;
	return null;
}

function trimZeros(value: string): string {
	return value.replace(/\.?0+$/, "");
}
